import { useArrayDifference } from "@vueuse/core";

import { defineStore } from "pinia";
import { computed, ref, toRef, watch } from "vue";
import { boothFields, dataSources, unitSystems } from "../data";

import { BOOTH_STATUSES } from "../lib/enums";
import { formatLengthUnit, getUniqueValues, wait } from "../lib/utils";
import { useUsersStore } from "../stores/users";

import { getEvent, updateBooth } from "../lib/endpoints";
import {
  reserveBooths,
  fetchLocalDataDump,
  sync,
  fetchRainfocusDataDump,
} from "../lib/endpoints_rainfocus";
import papaparse from "papaparse";

export const UX_STEPS = {
  LOADING: "loading",
  NO_SPONSOR: "no-sponsor",
  WELCOME: "welcome",
  FORM: "form",
  SUCCESS: "success",
  ASSIGNED: "assigned",
};

export const SET_LABEL_POS = {
  TOP: "TOP",
  BOTTOM: "BOTTOM",
};

const defaultSettingLabelPos = { type: null, position: null };

export const useBoothsStore = defineStore("booths", () => {
  const event = ref();
  const rawBooths = ref([]);
  const floorData = ref();
  const selectedBoothId = ref(null);
  const debouncedSelectedBoothId = ref(null);
  const hoveredBoothId = ref(null);
  const requestingBoothsIds = ref([]);
  const currentStep = ref(UX_STEPS.LOADING);
  const exhibitorHasBookedBooth = ref(false);
  const settingLabelPos = ref(defaultSettingLabelPos);

  async function setSelectedBoothId(id) {
    // reset label positing if booth changed
    if (!id || selectedBoothId.value !== id)
      settingLabelPos.value = defaultSettingLabelPos;

    // check this booth is interactable
    selectedBoothId.value = id;
    if (debouncedSelectedBoothId.value && id) {
      debouncedSelectedBoothId.value = null;
      await wait(150);
    }
    debouncedSelectedBoothId.value = id;
  }

  function setRequestingBoothsId(id) {
    const isIncluded = requestingBoothsIds.value.includes(id);
    requestingBoothsIds.value = !isIncluded
      ? [...requestingBoothsIds.value, id]
      : requestingBoothsIds.value.filter((_id) => _id !== id);
  }

  async function doUpdateBooth(id, data) {
    try {
      let booth = await updateBooth(id, data);
      console.log("updated", booth, id, data);
      rawBooths.value = rawBooths.value.map((booth) => {
        if (booth.id === id) {
          booth = { ...booth, ...data };
        }
        return booth;
      });
    } catch (e) {
      console.log("error updating booth", e);
    }
  }

  function getBoothInfo(booth, fields) {
    booth = toRef(booth);
    if (!booth.value) return undefined;

    return fields.map((field) => {
      const value = booth.value[field];
      const fieldInfo = boothFields.get(field);
      const source = {
        id: fieldInfo.source,
        ...dataSources.get(fieldInfo.source),
      };
      return { ...fieldInfo, source, value };
    });
  }

  const eventLogo = computed(() =>
    event.value.logoUrl ? event.value.logoUrl.url.toString() : null,
  );
  const unitSystem = computed(() => {
    return unitSystems.get(event.value.unitSystem);
  });

  const selectedBooth = computed(() =>
    booths.value.find((booth) => booth.id === selectedBoothId.value),
  );

  const hoveredBooth = computed(() =>
    booths.value.find((booth) => booth.id === hoveredBoothId.value),
  );

  const booths = computed(() => {
    if (!event.value) return [];
    const { lengthUnit, areaUnit } = unitSystem.value;

    return rawBooths.value.map((booth) => {
      const widthInfo = formatLengthUnit(booth.size.x);
      const heightInfo = formatLengthUnit(booth.size.y);
      const status = booth.exhibitorId
        ? BOOTH_STATUSES.ASSIGNED
        : booth.isBlocked
          ? BOOTH_STATUSES.BLOCKED
          : BOOTH_STATUSES.AVAILABLE;

      return {
        ...booth,
        status,
        isAvailable: status === BOOTH_STATUSES.AVAILABLE,
        dimensions: `${widthInfo}×${heightInfo} ${lengthUnit}`,
        area: `${widthInfo * heightInfo} ${areaUnit}`,
      };
    });
  });

  const allStatuses = ref([
    BOOTH_STATUSES.AVAILABLE,
    BOOTH_STATUSES.BLOCKED,
    BOOTH_STATUSES.ASSIGNED,
  ]);

  const allSponsorLevels = computed(() => {
    return getUniqueValues(rawBooths.value, "sponsorLevel");
  });

  const filterStatuses = ref(allStatuses.value);
  const filterSponsorLevels = ref([]);
  watch(allSponsorLevels, (value) => (filterSponsorLevels.value = value));

  const isAllStatuses = computed(
    () => !useArrayDifference(allStatuses, filterStatuses).value.length,
  );

  const isAllSponsorLevels = computed(
    () =>
      !useArrayDifference(allSponsorLevels, filterSponsorLevels).value.length,
  );

  function toggleAllStatuses() {
    filterStatuses.value = !isAllStatuses.value ? allStatuses.value : [];
  }

  function toggleAllSponsorLevels() {
    filterSponsorLevels.value = !isAllSponsorLevels.value
      ? allSponsorLevels.value
      : [];
  }

  const filteredBooths = computed(() => {
    return booths.value
      .filter((booth) => filterStatuses.value.includes(booth.status))
      .filter((booth) =>
        filterSponsorLevels.value.includes(booth.sponsorLevel),
      );
  });

  // TODO do not incldude the whole booth object -  just name, exhibitorId, eventId, id
  const requestingBooths = computed(() =>
    requestingBoothsIds.value
      .map((id) => booths.value.find((booth) => booth.id === id))
      .filter((booth) => booth !== undefined),
  );

  const pendingEventFetch = ref(null);

  async function fetchEvent(eventId) {
    if (event.value && event.value.id === eventId) return;

    if (!pendingEventFetch.value) {
      pendingEventFetch.value = getEvent(eventId);
    }

    try {
      const _event = await pendingEventFetch.value;
      event.value = _event;
      if (_event.floor) floorData.value = _event.floor;
    } finally {
      pendingEventFetch.value = null;
    }
  }

  async function fetchBooths(eventId) {
    const { exhibitorAccount, account } = useUsersStore();
    await fetchEvent(eventId);

    const dbBooths = event.value.boothsRelated.filter(
      (booth) => !booth.archived,
    );
    // const figmaBooths = JSON.parse(event.value.booths);

    const exhibitorHasBookedBooth =
      exhibitorAccount?.exhibitorId && exhibitorAccount.assignedBoooth?.name;

    rawBooths.value = dbBooths
      .map((dbBooth) => {
        // const dbBooth = dbBooths.find((it) => it.name === figmaBooth.name);

        // if (!dbBooth) return;

        if (dbBooth.exhibitorId) {
          dbBooth.isBlocked = true;
          dbBooth.displayName = dbBooth.exhibitorName;
        }

        if (dbBooth.overrideDisplayName) {
          dbBooth.displayName = dbBooth.overrideDisplayName;
        }

        const matchingSponsorLevel =
          !(dbBooth.isBlocked || dbBooth.exhibitorId) &&
          (!!account?.id ||
            !exhibitorAccount?.level ||
            exhibitorAccount?.level
              .toLowerCase()
              .replace(/\s/g, "")
              .includes(
                dbBooth.sponsorLevel?.toLowerCase().replace(/\s/g, ""),
              ));
        const interactionEnabled =
          !!account?.id ||
          !exhibitorAccount?.exhibitorId ||
          (!exhibitorHasBookedBooth &&
            matchingSponsorLevel &&
            !dbBooth.exhibitorId);

        dbBooth.interactionEnabled = interactionEnabled;
        dbBooth.matchingSponsorLevel = matchingSponsorLevel;

        // avoid overlapping properties
        // delete figmaBooth.width;
        // delete figmaBooth.height;
        // delete figmaBooth.pos;

        return {
          ...dbBooth,
          // ...figmaBooth,
          matchingSponsorLevel,
          bookedByThisExhibitor:
            exhibitorAccount?.exhibitorId === dbBooth.exhibitorId,
          interactionEnabled,

          notes: dbBooth.notes || "",
        };
      })
      .filter((it) => it !== undefined);
  }

  async function requestBooths() {
    const { exhibitorAccount } = useUsersStore();

    const reservation = {
      rfApiProfileId: event.value.rfApiProfileId,
      exhibitorId: exhibitorAccount.exhibitorId,
      sponsorshipLevel: exhibitorAccount.level,
      eventId: event.value.id,
      timestamp: new Date().toISOString(),
      booths: requestingBooths.value,
      status: "draft",
    };

    const data = await reserveBooths(
      reservation.rfApiProfileId,
      reservation.exhibitorId,
      reservation.eventId,
      reservation.booths,
    );

    // TODO redirect from rainfocus redirect link
    return data;
  }

  async function rainfocusSync({ eventId, rfApiProfileId }) {
    try {
      const response = await sync(eventId, rfApiProfileId);

      if (response.error || (response.length > 0 && response[0].error)) {
        const error =
          response.error || (response.length > 0 && response[0].error);
        throw new Error(error);
      }
      return response.data;
    } catch (_error) {
      console.error("Error executing function", _error);
      throw _error;
    }
  }

  async function rainfocusDataDump(rfApiProfileId) {
    const response = await fetchRainfocusDataDump(rfApiProfileId);
    console.log(response);
    return response;
  }

  async function localDataDump(eventId) {
    const response = await fetchLocalDataDump(eventId);
    console.log(response);
    const booths = response.booths;
    const reservations = response.reservations; // TODO add reservations to csv

    const csvData = booths.map((booth) => {
      const row = {
        "Booth Name": booth.name, // First column as 'Booth Name'
        "RF Booth Id": booth.boothId || "", // Include boothId, default to empty string if not present
        ...booth, // Include other fields
      };
      delete row.name; // Remove duplicate 'name' field
      delete row.boothId; // Remove duplicate 'name' field
      return row;
    });

    csvData.sort((a, b) => {
      if (a["Booth Name"] < b["Booth Name"]) return -1;
      if (a["Booth Name"] > b["Booth Name"]) return 1;
      return 0;
    });

    const csv = papaparse.unparse(csvData);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `booths_${title.value}.csv`; // maybe add date to name formatDate(event.syncedAt)
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    return booths;
  }

  return {
    event,
    eventLogo,
    booths,
    floorData,
    selectedBoothId,
    debouncedSelectedBoothId,
    hoveredBooth,
    hoveredBoothId,
    requestingBoothsIds,
    selectedBooth,
    requestingBooths,
    currentStep,
    fetchBooths,
    requestBooths,
    setSelectedBoothId,
    setRequestingBoothsId,
    doUpdateBooth,
    getBoothInfo,
    filteredBooths,
    filterStatuses,
    filterSponsorLevels,
    allStatuses,
    allSponsorLevels,
    isAllStatuses,
    isAllSponsorLevels,
    toggleAllStatuses,
    toggleAllSponsorLevels,
    fetchEvent,
    exhibitorHasBookedBooth,
    rainfocusSync,
    rainfocusDataDump,
    localDataDump,
    settingLabelPos,
  };
});
